import React, { useEffect, useState, useRef, useMemo } from "react";
import { Input } from "../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../shadcn/components/ui/button";
import { Label } from "../../../../../shadcn/components/ui/label";
import { Textarea } from "../../../../../shadcn/components/ui/textarea";
import { Dropdown } from "../../../../../shadcn/components/custom/Dropdown";
import { useSelector } from "react-redux";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { CompanyInfoParser } from "../utils/companyInfoParser";
import {
  fetchFuzzyMatchDetails,
  fetchIDCardDetails,
  fetchIDCardOCRDetails,
  getBidderByUuid,
  updateBidderForEvent,
} from "services/bidder";
import {
  getAddressProofOptionsByBidderType,
  getFileByUuid,
  normalizeBidderTypeForPanValidation,
} from "services";
import CustomModalLayout from "../../../../web/Auth/CustomModalLayout";
import CustomMultipleFileUploader from "../../../../banker/bidder/CustomMultipleFileUploader";
import CustomLoadingModal from "./IndividualBidders/CustomLoadingModal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  // DialogTrigger,
} from "../../../../../shadcn/components/ui/dialog";
// import { Calendar } from "../../../../../shadcn/components/ui/calendar";
// import CalendarDotsIcon from "../../../../../icons/CalendarDotsIcon";
import CheckIcon from "../../../../../icons/CheckIcon";
import EditIcon from "../../../../../icons/EditIcon";
import ExclamationIcon from "../../../../../icons/ExclamationIcon";
import ReusableDatePicker from "../../../../../shadcn/components/custom/ReusableDatePicker";

type CompanyInfoSources = {
  udyamData?: any;
  gstData?: any;
  cinData?: any;
};

interface BusinessDetailsComponentV3Props {
  stepsTitles?: any;
  activeStep?: any;
  setActiveStep?: any;
  bidderUuid?: any;
  init?: any;
  bidder?: any;
}

// Define kycModes outside component to avoid dependency issues
const kycModes = [
  { label: "Online", value: "online" },
  { label: "Offline", value: "offline" },
];

const normalizeConstitutionTypeValue = (value?: string) => {
  if (!value) return "";
  const normalized = value.toLowerCase().trim();

  if (
    normalized === "private_limited" ||
    normalized === "public_limited" ||
    normalized === "company"
  ) {
    return "company";
  }
  if (normalized === "llp") return "llpfirm";
  if (normalized === "partnership") return "partnershipfirm";
  if (
    normalized === "proprietorship" ||
    normalized === "proprietor" ||
    normalized === "proprietors"
  ) {
    return "proprietors";
  }
  if (normalized === "society" || normalized === "association_of_persons") {
    return "aop";
  }

  return normalized;
};

export default function BusinessDetailsComponentV3({
  stepsTitles,
  activeStep,
  setActiveStep,
  init,
  bidderUuid,
  bidder,
}: BusinessDetailsComponentV3Props) {
  // Define document types before using in state
  const documentTypes = [
    { label: "CIN", value: "CIN" },
    { label: "GST", value: "GST" },
    { label: "UDYAMAADHAAR", value: "UDYAMAADHAAR" },
  ];

  const [panFiles, setPanFiles] = useState<any>([]);
  const [addressProofFiles, setAddressProofFiles] = useState<any>([]);
  const [incorporationFiles, setIncorporationFiles] = useState<any>([]);
  const [moaFiles, setMoaFiles] = useState<any>([]);
  const [aoaFiles, setAoaFiles] = useState<any>([]);
  const [additionalFiles, setAdditionalFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);
  const [dateOpen, setDateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [fetchError, setFetchError] = useState<string>(""); // Specific error message from Fetch Details API
  const [validationErrorModelOpen, setValidationErrorModelOpen] =
    useState(false);
  const [validationErrorModelValue, setValidationErrorModelValue] = useState({
    source_text: "",
    target_text: "",
    mismatchSource: "document_fetch" as "document_fetch" | "pan_upload", // Track source of mismatch
    panFileId: "", // PAN file ID if from PAN upload
  });
  const [panNameMismatchModalOpen, setPanNameMismatchModalOpen] =
    useState(false);
  const [panNameMismatchModalValue, setPanNameMismatchModalValue] = useState({
    source_text: "",
    target_text: "",
    panFileId: "",
  });
  const [nameMismatchWarning, setNameMismatchWarning] = useState<string>("");
  const [panNameMismatchWarning, setPanNameMismatchWarning] =
    useState<string>("");
  const [panOCRData, setPanOCRData] = useState<any>({});
  const [isPanVerified, setIsPanVerified] = useState(false);
  const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);
  const [isLegalNameVerified, setIsLegalNameVerified] = useState(false);
  const [isLegalNameEditable, setIsLegalNameEditable] = useState(true);
  const [legalNameMismatchWarning, setLegalNameMismatchWarning] =
    useState<string>("");
  const [isVerifyingLegalName, setIsVerifyingLegalName] = useState(false);
  const [isDetailsFetched, setIsDetailsFetched] = useState(false);
  const [businessInfoData, setBusinessInfoData] = useState<any>(null); // Store full API response for business_info
  const [registeredPlace, setRegisteredPlace] = useState<string>(""); // Store registered place (extracted from document fetch)
  const [documentNumberError, setDocumentNumberError] = useState<string>(""); // Inline validation error for document number format
  const uploaderRef = useRef<any>(null);

  // Name mismatch modal state
  const [showNameMismatchModal, setShowNameMismatchModal] = useState(false);
  const [nameMismatchReason, setNameMismatchReason] = useState<string>("");
  const [nameMismatchSupportingDocuments, setNameMismatchSupportingDocuments] =
    useState<any[]>([]);
  const [nameMismatchConfirmed, setNameMismatchConfirmed] = useState(false);
  const [nameMismatchDetails, setNameMismatchDetails] = useState<{
    profileName: string;
    documentName: string;
    mismatchType: "pan" | "legal_name" | "both";
  }>({ profileName: "", documentName: "", mismatchType: "pan" });
  const supportingDocsUploaderRef = useRef<any>(null);

  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });

  const resolvedBidderUserType = normalizeBidderTypeForPanValidation(
    bidder?.meta?.user_details?.user_type?.value || onboardingData?.type,
  );

  // Get initial values from bidder
  const initialPan = bidder?.meta?.pan || "";
  const initialEmail = bidder?.email || "";
  const initialMobile = bidder?.meta?.user_details?.mobile_number || "";

  // Find the initial KYC mode option
  // const initialKycModeOption =
  //   kycModes.find((m) => m.value === initialKycMode) || kycModes[0];

  // Form initialization with all fields
  const form = useBetaForm({
    // Document verification fields (matching V2 naming)
    card_type: "",
    card_number: "",
    is_doc_verified: false,
    pan_uuid: "",

    // Business details fields
    constitution_type: "",
    kyc_mode: "",

    // Entity information fields
    legal_name: "",
    trade_name: "",
    registered_address: "",
    date_of_incorporation: "",

    // Contact and business info
    pan_of_entity: initialPan,
    nature_of_business: "",
    contact_email: initialEmail,
    contact_mobile: initialMobile,

    // Address proof
    address_proof_type: "",
    address_proof_uuid: "",

    // Document uploads
    incorporation_certificate_uuid: "",
    moa_uuid: [], // Array for MOA/primary secondary documents (generic for all types)
    aoa_uuid: [], // Array for AOA/secondary documents (generic for all types)
    additional_documents_uuid: [], // Array for multiple additional documents
    name_mismatch_reason: "", // Reason for name mismatch (for offline mode)
  });

  // Load existing data from bidder.kyc_data
  useEffect(() => {
    const loadExistingData = async () => {
      if (!bidder?.kyc_data || !bidderUuid) return;

      const kycData = bidder.kyc_data;
      const documentDetails = kycData.document_details || {};

      // Load form fields from saved data
      if (documentDetails.entity_type) {
        const normalizedEntityType = normalizeConstitutionTypeValue(
          documentDetails.entity_type,
        );
        const constitutionType = constitutionTypes.find(
          (ct) => ct.value === normalizedEntityType,
        );
        if (constitutionType) {
          form.setField("constitution_type", constitutionType);
        }
      }

      if (documentDetails.document_type) {
        const docType = documentTypes.find(
          (dt) => dt.value === documentDetails.document_type,
        );
        if (docType) {
          form.setField("card_type", docType);
        }
      }

      if (documentDetails.document_value) {
        form.setField("card_number", documentDetails.document_value);
      }

      if (kycData.business_info && kycData.business_info !== "Na") {
        setBusinessInfoData(kycData.business_info);
        setIsDetailsFetched(true);

        // Extract legal name from business_info if available
        const businessInfo = kycData.business_info;
        const legalName =
          businessInfo?.company_data?.company_name ||
          businessInfo?.corporate_name ||
          businessInfo?.["Name of Enterprise"] ||
          "";
        if (legalName && !form.getField("legal_name")) {
          form.setField("legal_name", legalName);
          // If online mode and name matches, mark as verified
          const kycMode = bidder?.meta?.bid_application_flow_mode;
          if (kycMode === "online") {
            setIsLegalNameVerified(true);
            setIsLegalNameEditable(false);
          }
        }
      }

      if (documentDetails.address_details) {
        form.setField("registered_address", documentDetails.address_details);
      }

      if (documentDetails.incorporation_date) {
        form.setField(
          "date_of_incorporation",
          documentDetails.incorporation_date,
        );
        // Parse and set date
        const dateParts = documentDetails.incorporation_date.split("/");
        if (dateParts.length === 3) {
          const date = new Date(
            parseInt(dateParts[2]),
            parseInt(dateParts[1]) - 1,
            parseInt(dateParts[0]),
          );
          if (!isNaN(date.getTime())) {
            setSelectedDate(date);
          }
        }
      }

      if (documentDetails.business_nature) {
        form.setField("nature_of_business", documentDetails.business_nature);
      }

      if (documentDetails.place) {
        setRegisteredPlace(documentDetails.place);
      }

      // Load files from API
      try {
        // Load PAN files
        if (kycData.pan) {
          try {
            const panFile: any = await getFileByUuid(kycData.pan);
            if (panFile) {
              const formattedPanFile = {
                id: panFile?.uuid,
                filename:
                  panFile?.file_name || panFile?.filename || "PAN Document",
                mime: panFile?.mime || "",
              };
              setPanFiles([formattedPanFile]);
              form.setField("pan_uuid", kycData.pan);
              setIsPanVerified(true);
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error("Error loading PAN file:", error);
          }
        }

        // Load address proof type and files
        // Note: address_proof_type might not be stored in kyc_data, so we'll try to infer it
        // or use default if not available
        if (kycData.address_proof_uuid) {
          try {
            const addressProofFile: any = await getFileByUuid(
              kycData.address_proof_uuid,
            );
            if (addressProofFile) {
              const formattedAddressFile = {
                id: addressProofFile?.uuid,
                filename:
                  addressProofFile?.file_name ||
                  addressProofFile?.filename ||
                  "Address Proof",
                mime: addressProofFile?.mime || "",
              };
              setAddressProofFiles([formattedAddressFile]);
              form.setField("address_proof_uuid", kycData.address_proof_uuid);
              // Set default address proof type if not already set
              if (
                !form.getField("address_proof_type") &&
                addressProofTypes.length > 0
              ) {
                form.setField("address_proof_type", addressProofTypes[0]);
              }
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error("Error loading address proof file:", error);
          }
        }

        // Load incorporation certificate files
        if (kycData.incorporation_certificate_uuid) {
          try {
            const incorporationFile: any = await getFileByUuid(
              kycData.incorporation_certificate_uuid,
            );
            if (incorporationFile) {
              const formattedIncFile = {
                id: incorporationFile?.uuid,
                filename:
                  incorporationFile?.file_name ||
                  incorporationFile?.filename ||
                  "Incorporation Certificate",
                mime: incorporationFile?.mime || "",
              };
              setIncorporationFiles([formattedIncFile]);
              form.setField(
                "incorporation_certificate_uuid",
                kycData.incorporation_certificate_uuid,
              );
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error(
              "Error loading incorporation certificate file:",
              error,
            );
          }
        }

        // Load MOA files
        if (
          kycData.moa_uuid &&
          Array.isArray(kycData.moa_uuid) &&
          kycData.moa_uuid.length > 0
        ) {
          try {
            const moaFilesPromises = kycData.moa_uuid.map((uuid: string) =>
              getFileByUuid(uuid),
            );
            const moaFilesData: any = await Promise.all(moaFilesPromises);
            if (moaFilesData && moaFilesData.length > 0) {
              const formattedMoaFiles = moaFilesData.map((item: any) => ({
                id: item?.uuid,
                filename: item?.file_name || item?.filename || "MOA Document",
                mime: item?.mime || "",
              }));
              setMoaFiles(formattedMoaFiles);
              form.setField("moa_uuid", kycData.moa_uuid);
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error("Error loading MOA files:", error);
          }
        }

        // Load AOA files
        if (
          kycData.aoa_uuid &&
          Array.isArray(kycData.aoa_uuid) &&
          kycData.aoa_uuid.length > 0
        ) {
          try {
            const aoaFilesPromises = kycData.aoa_uuid.map((uuid: string) =>
              getFileByUuid(uuid),
            );
            const aoaFilesData: any = await Promise.all(aoaFilesPromises);
            if (aoaFilesData && aoaFilesData.length > 0) {
              const formattedAoaFiles = aoaFilesData.map((item: any) => ({
                id: item?.uuid,
                filename: item?.file_name || item?.filename || "AOA Document",
                mime: item?.mime || "",
              }));
              setAoaFiles(formattedAoaFiles);
              form.setField("aoa_uuid", kycData.aoa_uuid);
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error("Error loading AOA files:", error);
          }
        }

        // Load additional documents
        if (
          kycData.additional_documents_uuids &&
          Array.isArray(kycData.additional_documents_uuids) &&
          kycData.additional_documents_uuids.length > 0
        ) {
          try {
            const additionalFilesPromises =
              kycData.additional_documents_uuids.map((uuid: string) =>
                getFileByUuid(uuid),
              );
            const additionalFilesData: any = await Promise.all(
              additionalFilesPromises,
            );
            if (additionalFilesData && additionalFilesData.length > 0) {
              const formattedAdditionalFiles = additionalFilesData.map(
                (item: any) => ({
                  id: item?.uuid,
                  filename:
                    item?.file_name || item?.filename || "Additional Document",
                  mime: item?.mime || "",
                }),
              );
              setAdditionalFiles(formattedAdditionalFiles);
              form.setField(
                "additional_documents_uuid",
                kycData.additional_documents_uuids,
              );
              setRenderKey((prev) => prev + 1);
            }
          } catch (error) {
            console.error("Error loading additional documents:", error);
          }
        }

        // Load name mismatch reason if exists
        if (kycData.name_mismatch_reason) {
          form.setField("name_mismatch_reason", kycData.name_mismatch_reason);
          setNameMismatchReason(kycData.name_mismatch_reason);
        }

        // Load name mismatch confirmation status
        if (kycData.pan_name_mismatch_confirmed) {
          setNameMismatchConfirmed(true);
        }
      } catch (error) {
        console.error("Error loading existing files:", error);
      }
    };

    loadExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidder?.kyc_data, bidderUuid]);

  // Update form when bidder data changes
  useEffect(() => {
    if (bidder) {
      const kycMode = bidder.meta?.bid_application_flow_mode;
      const pan = bidder.meta?.pan || "";
      const email = bidder.email || "";
      const mobile = bidder.meta?.user_details?.mobile_number || "";

      if (kycMode) {
        const modeOption = kycModes.find((m) => m.value === kycMode);
        console.log("modeOption-----", modeOption);
        if (modeOption) {
          form.setField("kyc_mode", modeOption);
          // Prefill Legal Name if offline mode is selected
          if (
            modeOption.value === "offline" &&
            !form.getField("legal_name") &&
            onboardingData?.name
          ) {
            form.setField("legal_name", onboardingData.name);
          }
        }
      }

      if (pan) {
        form.setField("pan_of_entity", pan);
      }

      if (email) {
        form.setField("contact_email", email);
      }

      if (mobile) {
        form.setField("contact_mobile", mobile);
      }

      // Load legal name from kyc_data if exists
      if (bidder.kyc_data?.document_details?.address_details) {
        // Try to extract legal name from business_info if available
        if (
          bidder.kyc_data.business_info &&
          bidder.kyc_data.business_info !== "Na"
        ) {
          const businessInfo = bidder.kyc_data.business_info;
          const legalName =
            businessInfo?.company_data?.company_name ||
            businessInfo?.corporate_name ||
            businessInfo?.["Name of Enterprise"] ||
            "";
          if (legalName && !form.getField("legal_name")) {
            form.setField("legal_name", legalName);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidder]);
  useEffect(() => {
    const kycModeValue = form.getField("kyc_mode")?.value;
    const isOnline = kycModeValue === "online";
    const isOffline = kycModeValue === "offline";

    // Build validation rules object
    const validationRules: any = {
      kyc_mode: {
        presence: {
          allowEmpty: false,
          message: "^KYC Mode is required",
        },
      },
      constitution_type: {
        presence: {
          allowEmpty: false,
          message: "^Constitution Type is required",
        },
      },
      legal_name: {
        presence: {
          allowEmpty: false,
          message: "^Legal Name of Entity is required",
        },
      },
      registered_address: {
        presence: {
          allowEmpty: false,
          message: "^Registered Address is required",
        },
      },
      date_of_incorporation: {
        presence: {
          allowEmpty: false,
          message: "^Date of Incorporation is required",
        },
      },
      pan_uuid: {
        presence: {
          allowEmpty: false,
          message: "^PAN of Entity document is required",
        },
      },
      address_proof_type: {
        presence: {
          allowEmpty: false,
          message: "^Address Proof Type is required",
        },
      },
      nature_of_business: {
        presence: {
          allowEmpty: false,
          message: "^Nature of Business is required",
        },
      },
      address_proof_uuid: {
        presence: {
          allowEmpty: false,
          message: "^Address Proof document is required",
        },
      },
      incorporation_certificate_uuid: {
        presence: {
          allowEmpty: false,
          message: "^Certificate of Incorporation document is required",
        },
      },
      contact_email: {
        presence: {
          allowEmpty: false,
          message: "^Contact Email is required",
        },
        email: {
          message: "^Please enter a valid email address",
        },
      },
      contact_mobile: {
        presence: {
          allowEmpty: false,
          message: "^Contact Mobile is required",
        },
        format: {
          pattern: /^[0-9]{10}$/,
          message: "^Please enter a valid 10-digit mobile number",
        },
      },
    };

    // For online mode, add document verification fields
    if (isOnline) {
      validationRules.card_type = {
        presence: {
          allowEmpty: false,
          message: "^Document Type is required",
        },
      };
      validationRules.card_number = {
        presence: {
          allowEmpty: false,
          message: "^Document Number is required",
        },
      };
    }

    // Add MOA and AOA validation based on constitution type configuration
    const constitutionType = form.getField("constitution_type");
    const config = getDocumentConfig(constitutionType);

    if (config.moaMandatory) {
      validationRules.moa_uuid = {
        presence: {
          allowEmpty: false,
          message: `^${config.moaLabel} is required`,
        },
      };
    }

    if (config.aoaMandatory) {
      validationRules.aoa_uuid = {
        presence: {
          allowEmpty: false,
          message: `^${config.aoaLabel} is required`,
        },
      };
    }

    // Make additional documents mandatory if there's a name mismatch
    const hasNameMismatch =
      nameMismatchWarning || panNameMismatchWarning || legalNameMismatchWarning;
    if (hasNameMismatch) {
      // For offline mode, always require additional documents
      // For online mode, require if not confirmed
      if (
        isOffline ||
        !nameMismatchConfirmed ||
        nameMismatchSupportingDocuments.length === 0
      ) {
        validationRules.additional_documents_uuid = {
          presence: {
            allowEmpty: false,
            message:
              "^Supporting documents are required for name mismatch verification",
          },
        };
      }

      // For offline mode, require name mismatch reason
      if (isOffline) {
        validationRules.name_mismatch_reason = {
          presence: {
            allowEmpty: false,
            message: "^Please provide a reason for the name mismatch",
          },
        };
      }
    }

    // For offline mode, check legal name verification
    // Note: We'll check this manually in handleSaveAndContinue since custom validator is not supported

    form.setValidationRules(validationRules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.value]);

  // // Get selected values from form
  // const selectedKycMode = form.getField("kyc_mode");
  // const selectedDocumentType = form.getField("card_type");
  // const documentNumber = form.getField("card_number");

  // const constitutionTypes = [
  //   { label: "Private Limited Company", value: "private_limited" },
  //   { label: "Public Limited Company", value: "public_limited" },
  //   { label: "LLP", value: "llp" },
  //   { label: "Partnership Firm", value: "partnership" },
  //   { label: "Proprietorship", value: "proprietorship" },
  //   { label: "Trust", value: "trust" },
  //   { label: "Society / Association / Club", value: "society" },
  //   { label: "HUF", value: "huf" },
  // ];
  const constitutionTypes = useMemo(
    () => [
      { label: "Private Limited Company", value: "private_limited" },
      { label: "Public Limited Company", value: "public_limited" },
      { label: "Limited Liability Partnership (LLP)", value: "llpfirm" },
      { label: "Partnership Firm", value: "partnershipfirm" },
      { label: "Proprietors", value: "proprietors" },
      { label: "Trust", value: "trust" },
      { label: "Association of Persons", value: "aop" },
      { label: "Body of Individuals", value: "boi" },
      { label: "Government Agency", value: "govt" },
      { label: "Local Authority", value: "local" },
      { label: "Hindu Undivided Family", value: "huf" },
      { label: "Artificial Juridical Person", value: "ajp" },
    ],
    [],
  );

  const shouldDisableConstitutionTypeDropdown =
    !!resolvedBidderUserType && resolvedBidderUserType !== "company";

  const constitutionTypeOptionsForDropdown = useMemo(() => {
    if (resolvedBidderUserType === "company") {
      return constitutionTypes.filter(
        (item) =>
          item.value === "private_limited" || item.value === "public_limited",
      );
    }
    return constitutionTypes;
  }, [constitutionTypes, resolvedBidderUserType]);

  useEffect(() => {
    if (!shouldDisableConstitutionTypeDropdown) return;

    const bidderTypeToConstitutionType: Record<string, string> = {
      proprietors: "proprietors",
      llpfirm: "llpfirm",
      partnershipfirm: "partnershipfirm",
      trust: "trust",
      huf: "huf",
      aop: "aop",
      boi: "boi",
      govt: "govt",
      local: "local",
      ajp: "ajp",
    };

    const mappedConstitutionType =
      bidderTypeToConstitutionType[resolvedBidderUserType];
    if (!mappedConstitutionType) return;

    const constitutionOption = constitutionTypes.find(
      (item) => item.value === mappedConstitutionType,
    );
    if (!constitutionOption) return;

    if (
      form.getField("constitution_type")?.value !== constitutionOption.value
    ) {
      form.setField("constitution_type", constitutionOption);
    }
  }, [
    constitutionTypes,
    form,
    resolvedBidderUserType,
    shouldDisableConstitutionTypeDropdown,
  ]);

  const addressProofTypes = useMemo(
    () => getAddressProofOptionsByBidderType(resolvedBidderUserType),
    [resolvedBidderUserType],
  );

  useEffect(() => {
    if (addressProofTypes.length === 0) return;

    const selectedAddressProofType = form.getField("address_proof_type");
    const selectedAddressProofTypeValue =
      typeof selectedAddressProofType === "object" &&
      selectedAddressProofType?.value
        ? selectedAddressProofType.value
        : selectedAddressProofType;

    const selectedOption = addressProofTypes.find(
      (item) => item.value === selectedAddressProofTypeValue,
    );

    if (!selectedOption) {
      form.setField("address_proof_type", addressProofTypes[0]);
    }
  }, [addressProofTypes, form]);

  const formatDate = (date: Date | undefined): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();
    return `${day}/${month}/${year}`;
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    form.setField("date_of_incorporation", date ? formatDate(date) : "");
    setDateOpen(false);
  };

  const getDocumentPlaceholder = (docType: string | null) => {
    if (!docType) return "Select document type first";
    switch (docType) {
      case "CIN":
        return "Enter CIN number (e.g., U12345XX2020PTC123456)";
      case "GST":
        return "Enter GSTIN number (e.g., 12ABCDE1234F1Z5)";
      case "UDYAMAADHAAR":
        return "Enter Udyam Aadhaar number (e.g., UDYAM-XX-XX-1234567)";
      default:
        return "Enter document number";
    }
  };

  const getDocumentLabel = (docType: string | null) => {
    if (!docType) return "Document Number";
    switch (docType) {
      case "CIN":
        return "CIN";
      case "GST":
        return "GSTIN";
      case "UDYAMAADHAAR":
        return "Udyam Aadhaar Number";
      default:
        return "Document Number";
    }
  };

  // Get document configuration based on constitution type
  const getDocumentConfig = (constitutionType: any) => {
    // Default configuration when no constitution type is selected
    const defaultConfig = {
      primaryLabel:
        "Upload Certificate of Incorporation / Primary Constitutional Document",
      primaryText: "Upload primary constitutional document",
      moaLabel: "Upload MOA / Primary Secondary Document",
      aoaLabel: "Upload AOA / Secondary Document",
      moaMandatory: false,
      aoaMandatory: false,
      addressProofText: "Upload Address Proof of Registered Office",
    };

    if (!constitutionType) {
      return defaultConfig;
    }

    try {
      const type =
        typeof constitutionType === "object" && constitutionType?.value
          ? constitutionType.value
          : constitutionType;
      const normalizedType = normalizeConstitutionTypeValue(type);

      switch (normalizedType) {
        case "company":
          return {
            primaryLabel:
              "Upload Certificate of Incorporation / Primary Constitutional Document",
            primaryText: "Upload primary constitutional document",
            moaLabel: "Upload MOA (Memorandum of Association)",
            aoaLabel: "Upload AOA (Articles of Association)",
            moaMandatory: true,
            aoaMandatory: true,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "llpfirm":
          return {
            primaryLabel: "Upload LLP Incorporation Certificate",
            primaryText: "Upload primary constitutional document",
            moaLabel: "Upload LLP Agreement (latest executed)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: true,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "partnershipfirm":
          return {
            primaryLabel: "Upload Partnership Deed (latest executed)",
            primaryText: "Upload primary constitutional document",
            moaLabel: "Upload Firm Registration Certificate (if registered)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: false,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "proprietors":
          return {
            primaryLabel: "Upload Business Proof (GST / Shop Act / Udyam etc.)",
            primaryText: "Upload primary constitutional document",
            moaLabel:
              "Upload any additional registration / trade licence (if applicable)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: false,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "trust":
          return {
            primaryLabel: "Upload Registered Trust Deed",
            primaryText: "Upload primary constitutional document",
            moaLabel:
              "Upload Trust Registration Certificate / approval (if any)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: false,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "aop":
        case "boi":
        case "govt":
        case "local":
        case "ajp":
          return {
            primaryLabel:
              "Upload Certificate of Incorporation / Primary Constitutional Document",
            primaryText: "Upload primary constitutional document",
            moaLabel:
              "Upload MOA / AOA or additional registration document (if applicable)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: false,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        case "huf":
          return {
            primaryLabel: "Upload HUF Declaration",
            primaryText: "Upload primary constitutional document",
            moaLabel:
              "Upload any additional registration / PAN documents (if applicable)",
            aoaLabel:
              "Upload any additional registration document (if applicable)",
            moaMandatory: false,
            aoaMandatory: false,
            addressProofText: "Upload Address Proof of Registered Office",
          };
        default:
          return defaultConfig;
      }
    } catch (error) {
      console.error("Error getting document config:", error);
      return defaultConfig;
    }
  };

  // Get address proof upload text based on selected address proof type
  const getAddressProofUploadText = (addressProofType: any) => {
    const proofType =
      typeof addressProofType === "object" && addressProofType?.value
        ? addressProofType.value
        : addressProofType;

    if (proofType === "gst_certificate") {
      return "Upload GST Registration Certificate - this may also be used as address proof and for address fetch";
    }

    return "Upload Address Proof of Registered Office";
  };

  // Sub-task 1.2: Document number format validation
  // Validates document number format before allowing Fetch Details API call
  const validateDocumentNumber = (
    docType: string | null,
    docNumber: string,
  ): string => {
    if (!docType || !docNumber) return "";

    const trimmedNumber = docNumber.trim();

    switch (docType) {
      case "CIN": {
        // CIN format: L/U + 5 digits + 2 letters + 4 digits + 3 letters + 6 digits + optional check digit
        // Example: U12345MH2020PTC123456
        const cinRegex = /^[A-Z]{1}\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;
        if (!cinRegex.test(trimmedNumber)) {
          return "Invalid CIN number";
        }
        return "";
      }
      case "GST": {
        // GSTIN format: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
        // Example: 22AAAAA0000A1Z5
        const gstRegex =
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(trimmedNumber)) {
          return "Invalid GST number";
        }
        return "";
      }
      case "UDYAMAADHAAR": {
        // Udyam Aadhaar format: UDYAM-XX-XX-1234567
        // Example: UDYAM-MH-01-0012345
        const udyamRegex = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;
        if (!udyamRegex.test(trimmedNumber)) {
          return "Invalid Udyam Aadhaar number";
        }
        return "";
      }
      default:
        return "";
    }
  };

  const handleFetchDetails = async () => {
    // Sub-task 1.2: Validate document number format before API call
    const docType = form.getField("card_type")?.value;
    const docNumber = form.getField("card_number");
    const validationError = validateDocumentNumber(docType, docNumber);
    if (validationError) {
      setDocumentNumberError(validationError);
      return; // Don't proceed with API call if format is invalid
    }
    setDocumentNumberError(""); // Clear any previous validation error

    setLoading(true);
    setFetchError("");
    setLoadingText("Fetching business details. Please wait...");
    setIsOcrLoading(true);

    try {
      const payload = {
        card_type: form.getField("card_type")?.value,
        card_number: form.getField("card_number"),
      };

      const res: any = await fetchIDCardDetails(payload);

      // Sub-task 1.3: Check if API returned empty/null/no useful data
      if (!res || (typeof res === "object" && Object.keys(res).length === 0)) {
        setLoading(false);
        setLoadingText("");
        setIsOcrLoading(false);
        setIsDetailsFetched(false);
        setFetchError(
          "Something went wrong while fetching details. Please try again or fill in the details manually.",
        );
        return;
      }

      const type = form.getField("card_type")?.value;
      const validTypes: Record<string, keyof CompanyInfoSources> = {
        CIN: "cinData",
        GST: "gstData",
        UDYAMAADHAAR: "udyamData",
      };

      const key = validTypes[type];
      if (!key) throw new Error("Data not found or unsupported card_type");

      const parser = new CompanyInfoParser({ [key]: res });

      // Fuzzy match validation (optional - can be added later)
      const fuzzyMatchPayload: any = {
        context: "name",
        target_text: onboardingData?.name,
      };

      fuzzyMatchPayload["source_text"] =
        type === "CIN"
          ? res?.company_data?.company_name
          : type === "GST"
          ? res?.corporate_name
          : type === "UDYAMAADHAAR"
          ? res["Name of Enterprise"]
          : "";

      if (fuzzyMatchPayload["source_text"] && onboardingData?.name) {
        const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);
        if (!res2?.matched) {
          // Name mismatch detected during document fetch
          const documentName = fuzzyMatchPayload["source_text"] || "";
          setNameMismatchWarning(
            `Name mismatch detected. Document name: ${documentName?.toUpperCase()}, Profile Name is: ${onboardingData?.name?.toUpperCase()}. Please ensure the document belongs to your profile.`,
          );
          setLoading(false);
          setLoadingText("");
          setIsOcrLoading(false);
          setIsDetailsFetched(false); // Don't mark as fetched if name mismatch

          // Check if online mode - use previous ValidationErrorModel
          const kycMode = form.getField("kyc_mode")?.value;
          if (kycMode === "online") {
            // Online mode - use previous ValidationErrorModel
            // Mark as from document_fetch so it only clears document fetch fields
            setValidationErrorModelValue({
              source_text: documentName,
              target_text: onboardingData?.name || "",
              mismatchSource: "document_fetch",
              panFileId: "",
            });
            setValidationErrorModelOpen(true);
          }
          // In offline mode, just show warning - user will provide reason in form field
          return;
        } else {
          // Clear warning if name matches
          setNameMismatchWarning("");
        }
      }

      // Auto-fill form fields with fetched data
      const registeredAddress = parser.getRegisteredAddress();
      const extractedRegisteredPlace = parser.getRegisteredPlace();
      const incorporationDate = parser.getDateOfIncorporation();
      const businessNature = parser.getBusinessNature();
      const companyName =
        type === "CIN"
          ? res?.company_data?.company_name
          : type === "GST"
          ? res?.corporate_name
          : type === "UDYAMAADHAAR"
          ? res["Name of Enterprise"]
          : "";

      // Store business_info data (full API response) for online mode when name matches
      // This matches V2 behavior where business_info is the full API response
      setBusinessInfoData(res);

      // Update form fields
      if (companyName) {
        form.setField("legal_name", companyName);
        // Mark as verified and disable (but editable) in online mode
        if (form.getField("kyc_mode")?.value === "online") {
          setIsLegalNameVerified(true);
          setIsLegalNameEditable(false);
        }
      }
      if (registeredAddress) {
        form.setField("registered_address", registeredAddress);
      }
      if (extractedRegisteredPlace) {
        setRegisteredPlace(extractedRegisteredPlace);
      }
      if (incorporationDate) {
        // Parse date and set it
        const dateParts = incorporationDate.split("/");
        if (dateParts.length === 3) {
          const date = new Date(
            parseInt(dateParts[2]),
            parseInt(dateParts[1]) - 1,
            parseInt(dateParts[0]),
          );
          if (!isNaN(date.getTime())) {
            setSelectedDate(date);
            form.setField("date_of_incorporation", incorporationDate);
          }
        }
      }
      if (businessNature) {
        form.setField("nature_of_business", businessNature);
      }

      setLoadingText("");
      setLoading(false);
      setIsOcrLoading(false);
      setIsDetailsFetched(true); // Mark as successfully fetched
    } catch (error: any) {
      console.error("Error fetching business details:", error);
      setLoading(false);
      setLoadingText("");
      setIsOcrLoading(false);
      setIsDetailsFetched(false); // Reset on error

      setFetchError(
        "Something went wrong while fetching details. Please try again or fill in the details manually.",
      );
    }
  };

  const handlePanDocumentChange = async (data: any) => {
    if (data?.[0]?.id) {
      // Perform OCR for both online and offline modes
      const isOfflineMode = form.getField("kyc_mode")?.value === "offline";
      const isOnlineMode = form.getField("kyc_mode")?.value === "online";

      // Skip OCR only if no mode is selected
      if (!isOnlineMode && !isOfflineMode) {
        setPanFiles(data);
        form.setField("pan_uuid", data?.[0]?.id || "");
        return;
      }

      setLoading(true);
      setLoadingText("Verifying PAN details. Please wait...");
      setIsPanVerified(false);
      setPanNameMismatchWarning("");
      setIsOcrLoading(true);

      let res: any = null;
      let attempt = 0;
      let success = false;

      while (attempt < 3 && !success) {
        try {
          res = await fetchIDCardOCRDetails({
            enable_both_sides: false,
            check_id_match: false,
            front_part: data?.[0]?.id,
          });

          if (res?.detections?.length) {
            form.setErrors({ pan_uuid: [] });
            success = true;
          }
        } catch (error: any) {
          if (error?.response?.status === 400) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ pan_uuid: [message] });
            attempt = 4;
          }
          if (error?.response?.status >= 500) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ pan_uuid: [message] });
            attempt = 4;
          }
          attempt++;
          if (attempt < 3) {
            await new Promise((resolve) => setTimeout(resolve, 18000));
            if (attempt === 1) {
              setLoadingText(
                "This is taking longer than expected. Please wait a moment...",
              );
            }
          } else if (attempt === 3) {
            const message: any = error?.response?.data?.errorMessage;
            form.setErrors({ pan_uuid: [message] });
          }
        }
      }

      setIsOcrLoading(false);
      if (!success) {
        await uploaderRef.current?.deleteFile(data?.[0]?.id);
        setIsPanVerified(false);
        setLoading(false);
        setLoadingText("");
        return;
      }

      if (res?.detections?.length > 0) {
        const detection = res.detections[0];

        // Validate document type is PAN
        if (detection.id_type !== "PAN") {
          setIsOcrLoading(false);
          form.setErrors({
            pan_uuid: ["Please upload a valid PAN document."],
          });
          setLoading(false);
          await uploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }

        // Validate it's the front side
        if (detection?.detected_part !== "FRONT") {
          setIsOcrLoading(false);
          form.setErrors({
            pan_uuid: [
              "Upload a valid FRONT side of your PAN document to continue.",
            ],
          });
          setLoading(false);
          await uploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }
        if (
          detection?.id_attributes?.id_no?.toLowerCase() !==
          onboardingData?.pan?.toLowerCase()
        ) {
          setIsOcrLoading(false);
          form.setErrors({
            pan_uuid: [
              `The uploaded PAN (${
                detection?.id_attributes?.id_no ?? ""
              }) does not match the PAN (${
                onboardingData?.pan ?? ""
              }) registered in your profile.`,
            ],
          });
          setLoading(false);
          await uploaderRef.current?.deleteFile(data?.[0]?.id);
          return;
        }

        // Perform fuzzy name matching
        const fuzzyMatchPayload: any = {
          context: "name",
          target_text: onboardingData?.name,
          source_text: detection?.id_attributes?.name || "",
        };

        if (fuzzyMatchPayload["source_text"] && onboardingData?.name) {
          const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);
          if (!res2?.matched && res2?.match_score < 50) {
            setIsOcrLoading(false);
            form.setErrors({
              pan_uuid: [
                `Name mismatch below threshold. Document name: ${fuzzyMatchPayload[
                  "source_text"
                ]?.toUpperCase()}, Profile Name is: ${onboardingData?.name?.toUpperCase()}. The matching score is below the required threshold. Please contact support for assistance or try again.`,
              ],
            });
            setLoading(false);
            await uploaderRef.current?.deleteFile(data?.[0]?.id);
            return;
          } else if (!res2?.matched) {
            // Name mismatch detected
            setPanOCRData(res);
            setPanNameMismatchWarning(
              `Name mismatch detected. Document name: ${fuzzyMatchPayload[
                "source_text"
              ]?.toUpperCase()}, Profile Name is: ${onboardingData?.name?.toUpperCase()}. Please ensure the document belongs to your profile.`,
            );

            // In offline mode, only show warning (no modal)
            if (isOfflineMode) {
              setIsPanVerified(false);
              setLoading(false);
              setLoadingText("");
              setIsOcrLoading(false);
              // Save PAN file UUID even with mismatch in offline mode
              form.setField("pan_uuid", data?.[0]?.id || "");
              setPanFiles(data);
              return;
            } else {
              // Online mode - show ValidationErrorModel (same as document fetch)
              setIsPanVerified(false);
              setLoading(false);
              setLoadingText("");
              setIsOcrLoading(false);

              // Use ValidationErrorModel (same modal as document fetch)
              // But mark it as from PAN upload so it only clears PAN fields
              setValidationErrorModelValue({
                source_text: fuzzyMatchPayload["source_text"],
                target_text: onboardingData?.name || "",
                mismatchSource: "pan_upload",
                panFileId: data?.[0]?.id || "",
              });
              setValidationErrorModelOpen(true);
              // Don't save PAN file UUID - user must fix the issue or switch to offline
              // File will be deleted when user clicks "Discard and Retry"
              form.setField("pan_uuid", "");
              setPanFiles([]);
              return;
            }
          } else {
            // Name matches
            setPanNameMismatchWarning("");
            setIsPanVerified(true);
            setPanOCRData(res);
          }
        }
      }

      // Save PAN file UUID to form and update file list state
      form.setField("pan_uuid", data?.[0]?.id || "");
      setPanFiles(data);
      setLoadingText("");
      setLoading(false);
      setIsOcrLoading(false);
    }
  };

  const handleVerifyLegalName = async () => {
    const legalName = form.getField("legal_name");
    if (!legalName || !onboardingData?.name) {
      return;
    }

    setIsVerifyingLegalName(true);
    setLegalNameMismatchWarning("");

    try {
      const fuzzyMatchPayload: any = {
        context: "name",
        target_text: onboardingData?.name,
        source_text: legalName,
      };

      const res: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);

      if (!res?.matched) {
        // Name mismatch - show warning
        setLegalNameMismatchWarning(
          `Name mismatch detected. Legal Name: ${legalName?.toUpperCase()}, Profile Name is: ${onboardingData?.name?.toUpperCase()}. Please ensure the name is correct.`,
        );
        setIsLegalNameVerified(false);
        setIsLegalNameEditable(true);
      } else {
        // Name matches
        setLegalNameMismatchWarning("");
        setIsLegalNameVerified(true);
        setIsLegalNameEditable(false);
      }
    } catch (error) {
      console.error("Error verifying legal name:", error);
      setLegalNameMismatchWarning("Error verifying name. Please try again.");
    } finally {
      setIsVerifyingLegalName(false);
    }
  };

  const handleEditLegalName = () => {
    setIsLegalNameEditable(true);
    setIsLegalNameVerified(false);
    setLegalNameMismatchWarning("");
  };

  // Handle name mismatch modal confirmation
  const handleNameMismatchConfirm = () => {
    if (
      !nameMismatchReason ||
      nameMismatchReason.trim() === "" ||
      nameMismatchSupportingDocuments.length === 0
    ) {
      return;
    }

    setNameMismatchConfirmed(true);
    setShowNameMismatchModal(false);
    // Clear warnings after confirmation
    setNameMismatchWarning("");
    setPanNameMismatchWarning("");
    setLegalNameMismatchWarning("");
  };

  const handleSaveAndContinue = async () => {
    // First, check if Constitution Type is selected (must be first)
    const constitutionType = form.getField("constitution_type");
    if (!constitutionType || !constitutionType?.value) {
      form.setErrors({
        constitution_type: ["Please select Constitution Type first"],
      });
      // Scroll to constitution type field - use longer timeout to ensure DOM updates
      setTimeout(() => {
        // Try multiple selectors to find the constitution type field
        const errorElement =
          document.getElementById("constitution-type-field") ||
          document.querySelector('[placeholder*="Constitution Type"]') ||
          document.querySelector('[placeholder*="Select Constitution Type"]');

        if (errorElement) {
          // Scroll the element into view
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        } else {
          // Fallback: scroll to top of the form container
          const formContainer = document.querySelector("h4.font-bold.text-lg");
          if (formContainer) {
            formContainer.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }
      }, 200);
      return;
    }

    const kycMode = form.getField("kyc_mode")?.value;

    // For offline mode, check if legal name is verified
    if (kycMode === "offline" && !isLegalNameVerified) {
      form.setErrors({
        legal_name: ["Legal Name must be verified before submitting"],
      });
      // Scroll to legal name field
      setTimeout(() => {
        const errorElement =
          document.querySelector(
            `input[value="${form.getField("legal_name")}"]`,
          ) || document.querySelector('[placeholder*="Legal Name"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    // Form validation
    if (form.validate()) {
      // Additional validation for mandatory arrays (moa_uuid and aoa_uuid)
      // Constitution Type is already validated above, so it's safe to use
      const config = getDocumentConfig(constitutionType);
      const moaUuid = form.getField("moa_uuid");
      const aoaUuid = form.getField("aoa_uuid");

      if (config.moaMandatory) {
        const moaArray = Array.isArray(moaUuid) ? moaUuid : [];
        if (moaArray.length === 0) {
          form.setErrors({
            moa_uuid: [`${config.moaLabel} is required`],
          });
          // Scroll to MOA field
          setTimeout(() => {
            const errorElement = document.querySelector('[name="moa_uuid"]');
            if (errorElement) {
              errorElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 100);
          return;
        }
      }

      if (config.aoaMandatory) {
        const aoaArray = Array.isArray(aoaUuid) ? aoaUuid : [];
        if (aoaArray.length === 0) {
          form.setErrors({
            aoa_uuid: [`${config.aoaLabel} is required`],
          });
          // Scroll to AOA field
          setTimeout(() => {
            const errorElement = document.querySelector('[name="aoa_uuid"]');
            if (errorElement) {
              errorElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 100);
          return;
        }
      }

      // Check for name mismatches - different handling for online vs offline
      const hasNameMismatch =
        nameMismatchWarning ||
        panNameMismatchWarning ||
        legalNameMismatchWarning;

      if (hasNameMismatch) {
        if (kycMode === "offline") {
          // Offline mode: Allow name mismatch but require reason and additional documents
          const nameMismatchReasonValue = form.getField("name_mismatch_reason");
          const additionalDocs = form.getField("additional_documents_uuid");
          const additionalDocsArray = Array.isArray(additionalDocs)
            ? additionalDocs
            : [];

          if (
            !nameMismatchReasonValue ||
            nameMismatchReasonValue.trim() === ""
          ) {
            form.setErrors({
              name_mismatch_reason: [
                "Please provide a reason for the name mismatch",
              ],
            });
            setTimeout(() => {
              const errorElement = document.querySelector(
                '[name="name_mismatch_reason"]',
              );
              if (errorElement) {
                errorElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 100);
            return;
          }

          if (additionalDocsArray.length === 0) {
            form.setErrors({
              additional_documents_uuid: [
                "Supporting documents are required for name mismatch verification. Please upload supporting documents.",
              ],
            });
            setTimeout(() => {
              const errorElement = document.querySelector(
                '[name="additional_documents_uuid"]',
              );
              if (errorElement) {
                errorElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 100);
            return;
          }
        } else {
          // Online mode: Block submission if name mismatch exists
          // User must either fix the issue or switch to offline mode
          if (hasNameMismatch) {
            // Show error message
            alert(
              "Name mismatch detected. Please resolve the name mismatch by either:\n1. Discarding and uploading the correct document, or\n2. Switching to offline mode to continue with name mismatch.",
            );
            return;
          }
        }
      }

      // Set loading state when starting save process
      setLoading(true);
      setLoadingText("Saving business details. Please wait...");

      const data: any = await getBidderByUuid(bidderUuid);
      const constitutionTypeValue = form.getField("constitution_type")?.value;

      // Build kyc_data object
      const kycData: any = {
        business_info: businessInfoData || "Na",
        document_details: {
          document_type: form.getField("card_type")?.value || "",
          document_value: form.getField("card_number") || "",
          address_details: form.getField("registered_address") || "",
          incorporation_date: form.getField("date_of_incorporation") || "",
          gst_number:
            form.getField("card_type")?.value === "GST"
              ? form.getField("card_number")
              : "Na",
          business_nature: form.getField("nature_of_business") || "",
          entity_type: constitutionTypeValue || "",
          place: registeredPlace || "",
        },
        pan: form.getField("pan_uuid") || "", // PAN uuid of uploaded document
        address_proof_uuid: form.getField("address_proof_uuid") || "", // Address proof uuid of uploaded document
        incorporation_certificate_uuid:
          form.getField("incorporation_certificate_uuid") || "", // Incorporation certificate uuid of uploaded document
        // Always save moa_uuid and aoa_uuid as arrays for all constitution types (generic approach)
        moa_uuid: Array.isArray(form.getField("moa_uuid"))
          ? form.getField("moa_uuid")
          : [], // MOA/primary secondary document uuids array
        aoa_uuid: Array.isArray(form.getField("aoa_uuid"))
          ? form.getField("aoa_uuid")
          : [], // AOA/secondary document uuids array
        additional_documents_uuids: (() => {
          const regularDocs = Array.isArray(
            form.getField("additional_documents_uuid"),
          )
            ? form.getField("additional_documents_uuid")
            : [];
          // Include name mismatch supporting documents in additional documents
          const nameMismatchDocs = nameMismatchSupportingDocuments
            .map((doc: any) => doc?.id)
            .filter(Boolean);
          return [...regularDocs, ...nameMismatchDocs];
        })(), // Additional documents uuids array (includes name mismatch supporting docs)
        status: "completed",
        approval_status: "submitted",
        verification_status: "submitted",
        reject_remarks: null,
        pan_name_mismatch_confirmed:
          panNameMismatchWarning ||
          legalNameMismatchWarning ||
          nameMismatchWarning
            ? true
            : false,
        is_name_mismatch_supporting_documents_uploaded:
          form.getField("kyc_mode")?.value === "offline"
            ? Array.isArray(form.getField("additional_documents_uuid")) &&
              form.getField("additional_documents_uuid").length > 0
            : true, // Online mode - always true as user accepted via modal
        name_mismatch_reason:
          form.getField("kyc_mode")?.value === "offline"
            ? form.getField("name_mismatch_reason") || ""
            : "", // Online mode - reason not collected in previous modal
        name_mismatch_supporting_documents: [],
      };

      try {
        const res5: any = await updateBidderForEvent({
          uuid: bidderUuid,
          meta: {
            ...data?.meta,
            bid_application_flow_mode: form.getField("kyc_mode")?.value,
            steps_status: {
              ...data?.meta?.steps_status,
              business_details: "completed",
            },
          },
          kyc_data: kycData,
          verification_step: "business_details",
          status: "pending",
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setActiveStep(activeStep + 1);
        await init();
        setLoadingText("");
        setLoading(false);
      } catch (error: any) {
        console.error("Error saving business details:", error);
        setLoading(false);
        setLoadingText("");
        // Show error message to user
        const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Failed to save business details. Please try again.";
        alert(errorMessage);
      }
    } else {
      // Scroll to first error
      const firstErrorField = Object.keys(form.errors.all())[0];
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  return (
    <div>
      <h4 className="font-bold text-lg font-openSans">Business Details</h4>
      <div className="mt-4 min-h-[300px]">
        {/* Rejection Status UI */}
        {bidder?.kyc_data?.status === "rejected" && (
          <div className="mb-6">
            <div className="border-[1px] border-red-300 bg-red-50 px-4 py-3 rounded-md">
              <div className="flex gap-2 md:gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <ExclamationIcon className="text-red-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm md:text-base font-semibold text-red-700 mb-2">
                    Business Details Rejected
                  </div>
                  <div className="text-xs md:text-sm text-red-600 mb-2">
                    <span className="font-semibold">Rejection Remarks:</span>{" "}
                    {bidder?.kyc_data?.reject_remarks ||
                      "No rejection remarks provided"}
                  </div>
                  <div className="text-xs md:text-sm text-red-700 leading-relaxed">
                    Please review the remarks above and update your business
                    details accordingly.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs md:text-sm font-openSans text-brand-textGrey mb-4">
          Provide basic KYC details of the non-individual entity and upload
          mandatory documents.
        </div>

        {/* Top 4 Fields Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Constitution Type */}
          <div id="constitution-type-field">
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Constitution Type
            </Label>
            <Dropdown
              disabled={shouldDisableConstitutionTypeDropdown}
              dataSource={() => {
                return Promise.resolve(constitutionTypeOptionsForDropdown);
              }}
              defaultSelected={
                form.getField("constitution_type") !== ""
                  ? form.getField("constitution_type")
                  : ""
              }
              selectIdentifier={true}
              onChange={(value: any) => {
                form.setField("constitution_type", value);
              }}
              valueKey="value"
              placeholder="Select Constitution Type"
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `border-[1px] border-brand-borderLightGray rounded-sm text-sm leading-normal font-sans text-brand-texBlack px-4 h-[38px] w-full max-w-xl ${
                  shouldDisableConstitutionTypeDropdown
                    ? "bg-gray-100 cursor-not-allowed"
                    : ""
                }`,
              }}
            />
            {form.errors.get("constitution_type") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("constitution_type")[0]}
              </p>
            ) : null}
          </div>

          {/* KYC Mode */}
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              KYC Mode
            </Label>
            <Dropdown
              disabled={bidder?.meta?.bid_application_flow_mode === "offline"}
              dataSource={() => {
                return Promise.resolve(kycModes);
              }}
              defaultSelected={
                form.getField("kyc_mode") !== ""
                  ? form.getField("kyc_mode")
                  : ""
              }
              selectIdentifier={true}
              onChange={(value: any) => {
                form.setField("kyc_mode", value);
                // Clear PAN verification when mode changes
                // But keep warning when switching to offline (user may have name mismatch)
                if (value?.value === "offline") {
                  setIsPanVerified(false);
                  // Don't clear warning - it should persist when switching to offline
                  // Prefill Legal Name with onboarding name if not already set
                  if (!form.getField("legal_name") && onboardingData?.name) {
                    form.setField("legal_name", onboardingData.name);
                  }
                  // Clear legal name verification when switching to offline (user needs to verify again)
                  setIsLegalNameVerified(false);
                  setIsLegalNameEditable(true);
                  setLegalNameMismatchWarning("");
                } else if (value?.value === "online") {
                  setIsPanVerified(false);
                  // Clear warning when switching back to online (user wants to retry)
                  setPanNameMismatchWarning("");
                  // Clear legal name verification when switching to online
                  setIsLegalNameVerified(false);
                  setIsLegalNameEditable(true);
                  setLegalNameMismatchWarning("");
                  // Remove legal entity name when switching from offline to online
                  form.setField("legal_name", "");
                }
              }}
              valueKey="value"
              placeholder="Select KYC Mode"
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `border-[1px] border-brand-borderLightGray rounded-sm text-sm leading-normal font-sans text-brand-texBlack px-4 h-[38px] w-full max-w-xl ${
                  bidder?.meta?.bid_application_flow_mode === "offline"
                    ? "bg-gray-100 cursor-not-allowed"
                    : ""
                }`,
              }}
            />
            {form.getField("kyc_mode")?.value === "offline" && (
              <p className="text-xs text-brand-textGrey mt-2">
                Choosing offline means you need to submit the KYC documents at
                bank for verification.
              </p>
            )}
            {form.errors.get("kyc_mode") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("kyc_mode")[0]}
              </p>
            ) : null}
          </div>

          {/* Document Type Selection - Only show in online mode */}
          {form.getField("kyc_mode")?.value === "online" && (
            <div>
              <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
                Select document to verify business details
              </Label>
              <Dropdown
                dataSource={() => {
                  return Promise.resolve(documentTypes);
                }}
                defaultSelected={
                  form.getField("card_type") !== "" &&
                  form.getField("card_type") !== null
                    ? form.getField("card_type")
                    : null
                }
                selectIdentifier={true}
                onChange={(value: any) => {
                  form.setField("card_type", value);
                  form.setField("card_number", ""); // Clear the number when type changes

                  // Sub-task 1.1: Clear all prefetched data on document type change
                  // Reset form fields that were auto-filled by Fetch Details
                  form.setField("legal_name", "");
                  form.setField("registered_address", "");
                  form.setField("date_of_incorporation", "");
                  form.setField("nature_of_business", "");
                  setSelectedDate(undefined);
                  setBusinessInfoData(null);
                  setRegisteredPlace("");

                  // Reset verification and mismatch states
                  setIsLegalNameVerified(false);
                  setIsLegalNameEditable(true);
                  setLegalNameMismatchWarning("");

                  // Clear all warnings and error states
                  setNameMismatchWarning(""); // Clear warning when document type changes
                  setFetchError(""); // Clear any previous fetch errors
                  setDocumentNumberError(""); // Clear format validation error
                  setIsDetailsFetched(false); // Enable fetch button when document type changes
                }}
                valueKey="value"
                placeholder="Select Document Type"
                styles={{
                  hideDot: true,
                  dropdownItemsClass: "text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightPink",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  outerContainer:
                    "border-[1px] border-brand-borderLightGray rounded-sm text-sm leading-normal font-sans text-brand-texBlack px-4 h-[38px] w-full max-w-xl",
                }}
              />
              {form.errors.get("card_type") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("card_type")[0]}
                </p>
              ) : null}
            </div>
          )}

          {/* Dynamic Document Number Input - Only show in online mode */}
          {form.getField("kyc_mode")?.value === "online" && (
            <div>
              <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
                {getDocumentLabel(form.getField("card_type")?.value) ||
                  "Document Number"}
              </Label>
              <Input
                disabled={!form.getField("card_type")?.value}
                className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={
                  form.getField("card_type")?.value
                    ? getDocumentPlaceholder(form.getField("card_type")?.value)
                    : "Select document type first"
                }
                value={form.getField("card_number")}
                onChange={(e: any) => {
                  const upperValue = e?.target?.value?.toUpperCase();
                  form.setField("card_number", upperValue);
                  setNameMismatchWarning(""); // Clear warning when document number changes
                  setDocumentNumberError(""); // Clear format validation error on input change
                  setFetchError(""); // Clear fetch error on new input
                  setIsDetailsFetched(false); // Enable fetch button when document number changes
                }}
              />
              {documentNumberError ? (
                <p className="mt-1 text-sm text-red-500">
                  {documentNumberError}
                </p>
              ) : form.errors.get("card_number") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("card_number")[0]}
                </p>
              ) : null}
            </div>
          )}
        </div>

        {/* Fetch Details Button - Only show for online mode */}
        {form.getField("card_type")?.value &&
          form.getField("card_number") &&
          form.getField("kyc_mode")?.value === "online" && (
            <div className="flex">
              <div className="mb-6">
                <div className="flex">
                  <Button
                    disabled={loading || isDetailsFetched}
                    className=""
                    onClick={handleFetchDetails}
                  >
                    {loading ? "Fetching..." : "Fetch Details"}
                  </Button>
                </div>

                {loading && loadingText && (
                  <p className="text-xs text-brand-textGrey mt-1">
                    {loadingText}
                  </p>
                )}
                {!loading && (
                  <p className="text-xs text-brand-textGrey mt-1">
                    Will auto-fill name, address & incorporation date.
                  </p>
                )}
                {fetchError && (
                  <p className="mt-1 text-sm text-red-500">{fetchError}</p>
                )}
                {nameMismatchWarning && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs lg:text-sm text-yellow-800 font-medium">
                      ⚠️ Warning: {nameMismatchWarning}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Offline Mode Message */}
        {form.getField("card_type")?.value &&
          form.getField("card_number") &&
          form.getField("kyc_mode")?.value === "offline" && (
            <div className="mb-6">
              <div className="p-1 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800">
                  In offline mode, please fill all the business details manually
                  below. You will need to submit KYC documents at the bank for
                  verification.
                </p>
              </div>
            </div>
          )}

        {/* Rest of the Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Legal Name of Entity */}
          <div className="md:col-span-2">
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Legal Name of Entity
            </Label>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  disabled={
                    !form.getField("kyc_mode")?.value ||
                    ((form.getField("kyc_mode")?.value === "offline" ||
                      form.getField("kyc_mode")?.value === "online") &&
                      isLegalNameVerified &&
                      !isLegalNameEditable)
                  }
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter Legal Name"
                  value={form.getField("legal_name")}
                  onChange={(e: any) => {
                    form.setField("legal_name", e.target.value);
                    // Clear verification when user edits
                    if (isLegalNameVerified) {
                      setIsLegalNameVerified(false);
                      setLegalNameMismatchWarning("");
                    }
                  }}
                />
              </div>
              {/* Verify Button - Only show in offline mode */}
              {form.getField("kyc_mode")?.value === "offline" &&
                form.getField("legal_name") &&
                !isLegalNameVerified && (
                  <Button
                    type="button"
                    disabled={isVerifyingLegalName}
                    onClick={handleVerifyLegalName}
                    className="h-[38px] px-4 bg-brand-orange hover:bg-brand-orange/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isVerifyingLegalName ? "Verifying..." : "Verify"}
                  </Button>
                )}
              {/* Verified Icon and Edit Icon - Show in both online and offline mode when verified */}
              {(form.getField("kyc_mode")?.value === "offline" ||
                form.getField("kyc_mode")?.value === "online") &&
                isLegalNameVerified &&
                !isLegalNameEditable && (
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-5 w-5 text-green-500" />
                    <button
                      type="button"
                      onClick={handleEditLegalName}
                      className="p-2 text-primary hover:text-primary/80 hover:bg-gray-100 rounded transition-colors"
                      aria-label="Edit legal name"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
            </div>
            {form.errors.get("legal_name") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("legal_name")[0]}
              </p>
            ) : null}
            {legalNameMismatchWarning && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs lg:text-sm text-yellow-800 font-medium">
                  ⚠️ Warning: {legalNameMismatchWarning}
                </p>
              </div>
            )}
            {isLegalNameVerified && !legalNameMismatchWarning && (
              <p className="mt-1 text-sm text-green-500">
                Name verified successfully.
              </p>
            )}
          </div>

          {/* Trade Name */}
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Trade Name (optional)
            </Label>
            <Input
              disabled={!form.getField("kyc_mode")?.value}
              className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter Trade Name"
              value={form.getField("trade_name")}
              onChange={(e: any) => {
                form.setField("trade_name", e.target.value);
              }}
            />
            {form.errors.get("trade_name") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("trade_name")[0]}
              </p>
            ) : null}
          </div>

          {/* Date of Incorporation */}
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Date of Incorporation / Establishment
            </Label>
            {/* <Dialog open={dateOpen} onOpenChange={setDateOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!form.getField("kyc_mode")?.value}
                  className="flex gap-2 items-center w-full max-w-xl justify-between text-sm font-openSans text-brand-textLightGrey border-[1px] border-brand-borderLightGray h-[38px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {selectedDate ? (
                    formatDate(selectedDate)
                  ) : (
                    <span className="text-brand-textLightGrey">dd/mm/yyyy</span>
                  )}
                  <CalendarDotsIcon width={16} height={16} />
                </Button>
              </DialogTrigger>
              <DialogContent
                removeIcon={true}
                className="max-w-[340px] rounded-lg"
              >
                <DialogTitle className="hidden"> </DialogTitle>
                <DialogDescription className="hidden"></DialogDescription>
                <div className="font-openSans text-lg font-semibold text-brand-textLightGrey px-3">
                  Select Date of Incorporation
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </DialogContent>
            </Dialog> */}
            <ReusableDatePicker
              value={selectedDate}
              onDateChange={handleDateSelect}
              popupLabel="Select Date of Incorporation / Establishment"
              placeholder="dd/mm/yyyy"
              disabled={!form.getField("kyc_mode")?.value}
              fromYear={1900}
              toYear={new Date().getFullYear() + 2}
              className="max-w-xl"
              buttonClassName="h-[38px] border-[1px] border-brand-borderLightGray text-sm font-openSans text-brand-textLightGrey disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            {form.errors.get("date_of_incorporation") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("date_of_incorporation")[0]}
              </p>
            ) : null}
          </div>
        </div>

        {/* Registered Address - Full Width */}
        <div className="mt-4">
          <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
            Registered Address
          </Label>
          <Textarea
            disabled={!form.getField("kyc_mode")?.value}
            className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[100px] disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="Address line, city, state, PIN"
            value={form.getField("registered_address")}
            onChange={(e: any) => {
              form.setField("registered_address", e.target.value);
            }}
          />
          {form.errors.get("registered_address") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("registered_address")[0]}
            </p>
          ) : null}
        </div>
      </div>

      {/* Entity Information Section */}
      <div className="mt-8">
        <h4 className="font-bold text-lg font-openSans mb-4">
          Entity Information
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              PAN of Entity
            </Label>
            <Input
              disabled={
                !!bidder?.meta?.pan || !form.getField("kyc_mode")?.value
              }
              className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter PAN"
              value={form.getField("pan_of_entity")}
              onChange={(e: any) => {
                const upperValue = e?.target?.value?.toUpperCase();
                form.setField("pan_of_entity", upperValue);
              }}
            />
            {form.errors.get("pan_of_entity") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("pan_of_entity")[0]}
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Nature of Business / Activity
            </Label>
            <Input
              disabled={!form.getField("kyc_mode")?.value}
              className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter Nature of Business"
              value={form.getField("nature_of_business")}
              onChange={(e: any) => {
                form.setField("nature_of_business", e.target.value);
              }}
            />
            {form.errors.get("nature_of_business") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("nature_of_business")[0]}
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Contact Email
            </Label>
            <Input
              disabled={!!bidder?.email || !form.getField("kyc_mode")?.value}
              className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter Email"
              value={form.getField("contact_email")}
              onChange={(e: any) => {
                form.setField("contact_email", e.target.value);
              }}
            />
            {form.errors.get("contact_email") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("contact_email")[0]}
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Contact Mobile
            </Label>
            <Input
              disabled={
                !!bidder?.meta?.user_details?.mobile_number ||
                !form.getField("kyc_mode")?.value
              }
              className="border-[1px] border-brand-borderLightGray w-full max-w-xl py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter Mobile"
              value={form.getField("contact_mobile")}
              onChange={(e: any) => {
                form.setField("contact_mobile", e.target.value);
              }}
            />
            {form.errors.get("contact_mobile") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("contact_mobile")[0]}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mandatory KYC Documents Section */}
      <div className="mt-8">
        <h4 className="font-bold text-lg font-openSans mb-4">
          Mandatory KYC Documents
        </h4>

        {/* PAN of Entity Upload */}
        <div className="mb-4">
          <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
            PAN of Entity (mandatory)
          </Label>
          {!form.getField("constitution_type")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select Constitution Type first
            </div>
          ) : !form.getField("kyc_mode")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select KYC Mode first
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={`pan-${renderKey}`}
              handleChange={handlePanDocumentChange}
              allowedMimeTypes={["image/png", "image/jpeg"]}
              property_uuid={bidderUuid || ""}
              owner_service_type="ni_pan"
              data={panFiles}
              maxAllowed={1}
              defaultText="Upload PAN of the Entity"
              ref={uploaderRef}
            />
          )}
          {isPanVerified && (
            <p className="mt-1 text-sm text-green-500">
              Document is verified successfully.
            </p>
          )}
          {!isPanVerified && form.errors.get("pan_uuid") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("pan_uuid")[0]}
            </p>
          ) : null}
          {panNameMismatchWarning && (
            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs lg:text-sm text-yellow-800 font-medium">
                ⚠️ Warning: {panNameMismatchWarning}
              </p>
            </div>
          )}
        </div>

        {/* Address Proof */}
        <div className="mt-4 mb-4">
          <div className="mb-4">
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Address Proof Type
            </Label>
            <Dropdown
              disabled={
                !form.getField("constitution_type")?.value ||
                !form.getField("kyc_mode")?.value
              }
              dataSource={() => {
                return Promise.resolve(addressProofTypes);
              }}
              defaultSelected={
                form.getField("address_proof_type") !== ""
                  ? form.getField("address_proof_type")
                  : addressProofTypes[0]
              }
              selectIdentifier={true}
              onChange={(value: any) => {
                form.setField("address_proof_type", value);
              }}
              valueKey="value"
              placeholder="Select Address Proof Type"
              styles={{
                hideDot: true,
                dropdownItemsClass: "text-brand-borderGrey",
                hoverBgColor: "focus:bg-brand-lightPink",
                hoverTextColor: "focus:text-brand-borderGrey",
                outerContainer: `border-[1px] border-brand-borderLightGray rounded-sm text-sm leading-normal font-sans text-brand-texBlack px-4 h-[38px] w-full ${
                  !form.getField("constitution_type")?.value ||
                  !form.getField("kyc_mode")?.value
                    ? "bg-gray-100 cursor-not-allowed"
                    : ""
                }`,
              }}
            />
            {form.errors.get("address_proof_type") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("address_proof_type")[0]}
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
              Upload Address Proof
            </Label>
            {!form.getField("constitution_type")?.value ? (
              <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                Please select Constitution Type first
              </div>
            ) : !form.getField("kyc_mode")?.value ? (
              <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                Please select KYC Mode first
              </div>
            ) : (
              <CustomMultipleFileUploader
                key={`address-${renderKey}`}
                handleChange={(data: any) => {
                  setAddressProofFiles(data);
                  form.setField("address_proof_uuid", data?.[0]?.id || "");
                }}
                allowedMimeTypes={["image/png", "image/jpeg"]}
                property_uuid={bidderUuid || ""}
                owner_service_type="address_proof"
                data={addressProofFiles}
                maxAllowed={1}
                defaultText={getAddressProofUploadText(
                  form.getField("address_proof_type"),
                )}
              />
            )}
            {form.errors.get("address_proof_uuid") ? (
              <p className="mt-1 text-sm text-red-500">
                {form.errors.get("address_proof_uuid")[0]}
              </p>
            ) : null}
          </div>
        </div>

        {/* Certificate of Incorporation / Primary Constitutional Document */}
        <div className="mb-4">
          <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
            {getDocumentConfig(form.getField("constitution_type")).primaryLabel}
          </Label>
          {!form.getField("constitution_type")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select Constitution Type first
            </div>
          ) : !form.getField("kyc_mode")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select KYC Mode first
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={`incorporation-${renderKey}`}
              handleChange={(data: any) => {
                setIncorporationFiles(data);
                form.setField(
                  "incorporation_certificate_uuid",
                  data?.[0]?.id || "",
                );
              }}
              allowedMimeTypes={["image/png", "image/jpeg", "application/pdf"]}
              property_uuid={bidderUuid || ""}
              owner_service_type="incorporation_certificate"
              data={incorporationFiles}
              maxAllowed={1}
              defaultText={
                getDocumentConfig(form.getField("constitution_type"))
                  .primaryText
              }
            />
          )}
          {form.errors.get("incorporation_certificate_uuid") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("incorporation_certificate_uuid")[0]}
            </p>
          ) : null}
          <p className="text-xs text-brand-textGrey mt-2">
            Example: Partnership Deed, Trust Deed, Certificate of Incorporation
            etc.
          </p>
        </div>

        {/* MOA / Primary Secondary Document - Always shown for all types */}
        {(() => {
          const constitutionType = form.getField("constitution_type");
          const config = getDocumentConfig(constitutionType);
          const isMandatory = config.moaMandatory;

          return (
            <div className="mb-4">
              <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
                {config.moaLabel}
                {isMandatory && " (mandatory)"}
              </Label>
              {!constitutionType?.value ? (
                <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                  Please select Constitution Type first
                </div>
              ) : !form.getField("kyc_mode")?.value ? (
                <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                  Please select KYC Mode first
                </div>
              ) : (
                <CustomMultipleFileUploader
                  key={`moa-${renderKey}`}
                  handleChange={(data: any) => {
                    setMoaFiles(data);
                    const uuids =
                      data?.map((file: any) => file?.id).filter(Boolean) || [];
                    form.setField("moa_uuid", uuids);
                  }}
                  allowedMimeTypes={[
                    "image/png",
                    "image/jpeg",
                    "application/pdf",
                  ]}
                  property_uuid={bidderUuid || ""}
                  owner_service_type="moa"
                  data={moaFiles}
                  maxAllowed={5}
                  defaultText={config.moaLabel}
                />
              )}
              {form.errors.get("moa_uuid") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("moa_uuid")[0]}
                </p>
              ) : null}
            </div>
          );
        })()}

        {/* AOA / Secondary Document - Always shown for all types */}
        {(() => {
          const constitutionType = form.getField("constitution_type");
          const config = getDocumentConfig(constitutionType);
          const isMandatory = config.aoaMandatory;

          return (
            <div className="mb-4">
              <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
                {config.aoaLabel}
                {isMandatory && " (mandatory)"}
              </Label>
              {!constitutionType?.value ? (
                <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                  Please select Constitution Type first
                </div>
              ) : !form.getField("kyc_mode")?.value ? (
                <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
                  Please select KYC Mode first
                </div>
              ) : (
                <CustomMultipleFileUploader
                  key={`aoa-${renderKey}`}
                  handleChange={(data: any) => {
                    setAoaFiles(data);
                    const uuids =
                      data?.map((file: any) => file?.id).filter(Boolean) || [];
                    form.setField("aoa_uuid", uuids);
                  }}
                  allowedMimeTypes={[
                    "image/png",
                    "image/jpeg",
                    "application/pdf",
                  ]}
                  property_uuid={bidderUuid || ""}
                  owner_service_type="aoa"
                  data={aoaFiles}
                  maxAllowed={5}
                  defaultText={config.aoaLabel}
                />
              )}
              {form.errors.get("aoa_uuid") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("aoa_uuid")[0]}
                </p>
              ) : null}
              <p className="text-xs text-brand-textGrey mt-2">
                Example: MOA/AOA, Registration Certificate, Bye-laws, Committee
                Resolution etc.
              </p>
            </div>
          );
        })()}

        {/* Name Mismatch Reason Input - Only for Offline Mode */}
        {form.getField("kyc_mode")?.value === "offline" &&
          (nameMismatchWarning ||
            panNameMismatchWarning ||
            legalNameMismatchWarning) && (
            <div className="mb-4">
              <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
                Reason for Name Mismatch <span className="text-red-500">*</span>
              </Label>
              <Textarea
                disabled={!form.getField("kyc_mode")?.value}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[100px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Please provide a reason for the name mismatch (e.g., legal name change, rebranding, merger, etc.)"
                value={form.getField("name_mismatch_reason")}
                onChange={(e: any) => {
                  form.setField("name_mismatch_reason", e.target.value);
                }}
              />
              {form.errors.get("name_mismatch_reason") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("name_mismatch_reason")[0]}
                </p>
              ) : null}
              <p className="text-xs text-brand-textGrey mt-2">
                Required - Please explain why the name on your document(s)
                differs from your profile name.
              </p>
            </div>
          )}

        {/* Additional Supporting Documents */}
        <div className="mb-4">
          <Label className="text-sm font-openSans text-brand-textBlack mb-2 block">
            Additional Supporting Documents
            {(() => {
              const hasMismatch =
                nameMismatchWarning ||
                panNameMismatchWarning ||
                legalNameMismatchWarning;
              const isOffline = form.getField("kyc_mode")?.value === "offline";
              const isOnline = form.getField("kyc_mode")?.value === "online";

              if (hasMismatch) {
                if (isOffline) {
                  // Offline: mandatory if mismatch exists
                  return " (mandatory)";
                } else if (isOnline) {
                  // Online: mandatory if not confirmed
                  return !nameMismatchConfirmed ||
                    nameMismatchSupportingDocuments.length === 0
                    ? " (mandatory)"
                    : " (optional)";
                }
              }
              return " (optional)";
            })()}
          </Label>
          {!form.getField("constitution_type")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select Constitution Type first
            </div>
          ) : !form.getField("kyc_mode")?.value ? (
            <div className="border-[1px] border-brand-borderLightGray rounded bg-gray-100 p-4 text-center text-sm text-brand-textGrey cursor-not-allowed">
              Please select KYC Mode first
            </div>
          ) : (
            <CustomMultipleFileUploader
              key={`additional-${renderKey}`}
              handleChange={(data: any) => {
                setAdditionalFiles(data);
                const uuids =
                  data?.map((file: any) => file?.id).filter(Boolean) || [];
                form.setField("additional_documents_uuid", uuids);
              }}
              allowedMimeTypes={["image/png", "image/jpeg", "application/pdf"]}
              property_uuid={bidderUuid || ""}
              owner_service_type="additional_documents"
              data={additionalFiles}
              maxAllowed={5}
              defaultText="Upload any additional KYC / authorization documents"
            />
          )}
          {form.errors.get("additional_documents_uuid") ? (
            <p className="mt-1 text-sm text-red-500">
              {form.errors.get("additional_documents_uuid")[0]}
            </p>
          ) : null}
          <p className="text-xs text-brand-textGrey mt-2">
            {(() => {
              const hasMismatch =
                nameMismatchWarning ||
                panNameMismatchWarning ||
                legalNameMismatchWarning;
              const isOffline = form.getField("kyc_mode")?.value === "offline";
              const isOnline = form.getField("kyc_mode")?.value === "online";

              if (hasMismatch) {
                if (isOffline) {
                  return "Required - Please upload supporting documents for name mismatch verification.";
                } else if (isOnline) {
                  return !nameMismatchConfirmed ||
                    nameMismatchSupportingDocuments.length === 0
                    ? "Required - Please upload supporting documents for name mismatch verification."
                    : "Optional - use for any extra documents that lender may require.";
                }
              }
              return "Optional - use for any extra documents that lender may require.";
            })()}
          </p>
        </div>
      </div>

      {/* Save & Continue Button */}
      <div className="mt-12 flex justify-center">
        <Button
          disabled={
            !form.getField("constitution_type")?.value ||
            !form.getField("kyc_mode")?.value
          }
          className="w-full max-w-xl disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSaveAndContinue}
        >
          Save & Continue
        </Button>
      </div>

      {/* Name Mismatch Modal for Document Fetch and PAN Upload */}
      <ValidationErrorModel
        open={validationErrorModelOpen}
        setOpen={setValidationErrorModelOpen}
        form={form}
        source_text={validationErrorModelValue?.source_text}
        target_text={validationErrorModelValue?.target_text}
        kycModes={kycModes}
        mismatchSource={validationErrorModelValue?.mismatchSource}
        panFileId={validationErrorModelValue?.panFileId}
        uploaderRef={uploaderRef}
        setPanFiles={setPanFiles}
        setPanNameMismatchWarning={setPanNameMismatchWarning}
      />

      {/* Name Mismatch Modal for Non-Individual Entities */}
      <Dialog
        open={showNameMismatchModal}
        onOpenChange={setShowNameMismatchModal}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg text-brand-orange flex items-center gap-2">
              Name Mismatch Detected
            </DialogTitle>
            <DialogDescription className="text-xs lg:text-sm text-gray-600 mt-2">
              {nameMismatchDetails.mismatchType === "both"
                ? "There are name mismatches between your entity documents and your profile name."
                : `There is a name mismatch between your ${
                    nameMismatchDetails.mismatchType === "pan"
                      ? "PAN"
                      : "Legal Name"
                  } document and your profile name.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Name Comparison */}
            <div className="px-4 py-2 bg-yellow-50 border border-yellow-200 rounded">
              <div className="space-y-1">
                <p className="text-xs lg:text-sm font-semibold text-gray-700">
                  Profile Name:
                </p>
                <p className="text-xs lg:text-sm text-gray-600">
                  {nameMismatchDetails.profileName}
                </p>

                {(nameMismatchDetails.mismatchType === "pan" ||
                  nameMismatchDetails.mismatchType === "both") && (
                  <div className="mt-4 pt-4 border-t border-yellow-300">
                    <p className="text-xs lg:text-sm font-semibold text-gray-700">
                      PAN Document Name:
                    </p>
                    <p className="text-xs lg:text-sm text-gray-600">
                      {nameMismatchDetails.documentName || "N/A"}
                    </p>
                  </div>
                )}

                {nameMismatchDetails.mismatchType === "legal_name" && (
                  <div className="mt-4 pt-4 border-t border-yellow-300">
                    <p className="text-xs lg:text-sm font-semibold text-gray-700">
                      Legal Name on Document:
                    </p>
                    <p className="text-xs lg:text-sm text-gray-600">
                      {nameMismatchDetails.documentName || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Confirmation Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded">
              <input
                type="checkbox"
                id="name-mismatch-confirm"
                checked={nameMismatchConfirmed}
                onChange={(e) => setNameMismatchConfirmed(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="name-mismatch-confirm"
                className="text-sm text-gray-700 cursor-pointer"
              >
                I confirm that I am doing this intentionally and the document
                belongs to my entity. I understand that the name on the document
                may differ from my profile name due to legal name changes,
                rebranding, or other valid business reasons.
              </label>
            </div>

            {/* Name Mismatch Reason Input */}
            <div className="space-y-2">
              <Label
                htmlFor="name_mismatch_reason"
                className="text-sm font-semibold text-gray-700"
              >
                Reason for Name Mismatch <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-gray-500">
                Please provide a reason for the name mismatch (e.g., legal name
                change, rebranding, merger, etc.)
              </p>
              <Textarea
                id="name_mismatch_reason"
                placeholder="Enter reason for name mismatch"
                value={nameMismatchReason}
                onChange={(e) => setNameMismatchReason(e.target.value)}
                className="w-full border-[1px] border-brand-borderLightGray py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded min-h-[100px]"
                required
              />
            </div>

            {/* Supporting Documents Upload */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">
                Upload Supporting Documents{" "}
                <span className="text-red-500">*</span>
              </p>
              <p className="text-xs text-gray-500">
                Please upload any supporting documents (e.g., name change
                certificate, rebranding documents, merger documents, gazette
                notification, etc.) that verify why the name on your document(s)
                may differ from your profile name. You can upload any document
                type.
              </p>
              <CustomMultipleFileUploader
                handleChange={(data: any) => {
                  setNameMismatchSupportingDocuments(data || []);
                }}
                allowedMimeTypes={[
                  "application/pdf",
                  "image/png",
                  "image/jpeg",
                  "image/jpg",
                  "image/gif",
                  "image/webp",
                ]}
                property_uuid={bidderUuid || ""}
                owner_service_type="name_mismatch_supporting"
                data={nameMismatchSupportingDocuments}
                maxAllowed={5}
                defaultText="Upload supporting documents (PDF, Images, etc.)"
                ref={supportingDocsUploaderRef}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNameMismatchModal(false);
                setNameMismatchReason("");
                setNameMismatchSupportingDocuments([]);
                setNameMismatchConfirmed(false);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleNameMismatchConfirm}
              disabled={
                loading ||
                !nameMismatchReason ||
                nameMismatchReason.trim() === "" ||
                !nameMismatchConfirmed ||
                nameMismatchSupportingDocuments.length === 0
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              Confirm & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Modal for OCR and Data Fetching */}
      <CustomLoadingModal
        isOpen={isOcrLoading || loading}
        loadingText={loadingText || "Processing, please wait..."}
      />
    </div>
  );
}

interface PanNameMismatchModalProps {
  open: any;
  setOpen: any;
  source_text: string;
  target_text: string;
  panFileId: string;
  form: any;
  uploaderRef: any;
  setPanNameMismatchWarning: any;
  setPanFiles: any;
  kycModes: any[];
}

const PanNameMismatchModal = ({
  open,
  setOpen,
  source_text,
  target_text,
  panFileId,
  form,
  uploaderRef,
  setPanNameMismatchWarning,
  setPanFiles,
  kycModes,
}: PanNameMismatchModalProps) => {
  const handleDiscard = async () => {
    // Delete PAN file
    if (panFileId && uploaderRef?.current) {
      await uploaderRef.current?.deleteFile(panFileId);
    }
    form.setField("pan_uuid", "");
    setPanFiles([]);
    setPanNameMismatchWarning("");
    setOpen(false);
  };

  const handleSwitchToOffline = async () => {
    // Switch to offline mode
    const offlineMode = kycModes.find((m) => m.value === "offline");
    if (offlineMode) {
      form.setField("kyc_mode", offlineMode);
    }
    // Keep PAN data and warning
    setOpen(false);
  };

  const handleClose = () => {
    // When modal is closed without action, keep the warning so user can't proceed
    // User must either discard or switch to offline - don't allow closing without action
    // In online mode, we cannot allow proceeding with name mismatch
  };

  return (
    <CustomModalLayout
      open={open}
      onClose={handleClose}
      childrenClassName="pb-6 px-6 lg:px-8 overflow-y-scroll"
      className="pt-8 lg:pb-[30px] mx-4 w-full md:w-[420px] max-h-[calc(100%-10px)]"
    >
      <div className="flex flex-col justify-between min-h-[243px]">
        <div className="flex flex-col gap-4">
          <h2 className="font-semibold text-lg text-brand-textBlack text-center">
            Action Required
          </h2>
          <p className="text-sm text-brand-textBlack text-center leading-5 tracking-wide">
            Name validation failed. Our records show the name on your PAN
            document is{" "}
            <strong className="text-brand-orange">{source_text}</strong>, but it
            was verified as <strong>{target_text}</strong>. Please contact the
            Auction Bazaar support team for assistance.
          </p>
        </div>

        <div className="flex flex-col gap-4 mt-4">
          {/* Discard Option */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-brand-textBlack text-center">
              Entered incorrect details?
            </span>
            <span
              className="text-xs text-primary underline cursor-pointer font-medium"
              onClick={handleDiscard}
            >
              Discard and Retry
            </span>
          </div>

          {/* Continue to Offline Option */}
          <div className="flex flex-col items-center gap-2 mt-2 pt-4 border-t border-brand-borderLightGray">
            <p className="text-[10px] text-brand-textBlack text-center leading-4">
              To continue with name mismatch, switch to offline mode.
              You&apos;ll need to submit KYC documents at the bank for
              verification.
            </p>
            <span
              className="text-xs text-primary underline cursor-pointer font-medium"
              onClick={handleSwitchToOffline}
            >
              Switch to Offline Verification
            </span>
          </div>
        </div>
      </div>
    </CustomModalLayout>
  );
};

interface ValidationErrorModelProps {
  open: any;
  setOpen: any;
  source_text: string;
  target_text: string;
  form: any;
  kycModes?: any[];
  mismatchSource?: "document_fetch" | "pan_upload";
  panFileId?: string;
  uploaderRef?: any;
  setPanFiles?: any;
  setPanNameMismatchWarning?: any;
}

const ValidationErrorModel = ({
  open,
  setOpen,
  source_text,
  target_text,
  form,
  kycModes = [],
  mismatchSource = "document_fetch",
  panFileId = "",
  uploaderRef,
  setPanFiles,
  setPanNameMismatchWarning,
}: ValidationErrorModelProps) => {
  const handleDiscardAndRetry = async () => {
    if (mismatchSource === "pan_upload") {
      // PAN upload mismatch - only clear PAN fields
      if (panFileId && uploaderRef?.current) {
        await uploaderRef.current?.deleteFile(panFileId);
      }
      form.setField("pan_uuid", "");
      if (setPanFiles) setPanFiles([]);
      if (setPanNameMismatchWarning) setPanNameMismatchWarning("");
    } else {
      // Document fetch mismatch - only clear document fetch fields
      form.setField("card_number", "");
    }
    setOpen(false);
  };

  const handleContinueToOffline = async () => {
    // Switch to offline mode
    const offlineMode = kycModes.find((m) => m.value === "offline");
    if (offlineMode) {
      form.setField("kyc_mode", offlineMode);
      if (panFileId && uploaderRef?.current) {
        await uploaderRef.current?.deleteFile(panFileId);
      }
      form.setField("pan_uuid", "");
      if (setPanFiles) setPanFiles([]);
      if (setPanNameMismatchWarning) setPanNameMismatchWarning("");
    }
    setOpen(false);
  };

  const handleClose = () => {
    // Cannot close modal without action in online mode
    // User must either discard or switch to offline
  };

  return (
    <CustomModalLayout
      open={open}
      onClose={handleClose}
      showCross={false}
      childrenClassName="pb-6 px-4 overflow-y-scroll"
      className="pt-8 lg:pb-[30px] mx-4 w-full md:w-[420px] max-h-[calc(100%-10px)]"
    >
      <div className="flex flex-col justify-between min-h-[243px]">
        <div className="flex flex-col gap-4">
          <h2 className="font-semibold text-lg text-brand-textBlack text-center">
            Action Required
          </h2>
          <p className="text-xs md:text-sm text-brand-textBlack text-center font-openSans my-6">
            Name validation failed. Our records show the name on your document
            is <strong className="text-brand-orange">{source_text}</strong>, but
            it was verified as <strong>{target_text}</strong>. Please contact
            the Auction Bazaar support team for assistance.
          </p>
        </div>

        <div className="flex flex-col gap-4 mt-4">
          {/* Discard and Retry Option */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-brand-textBlack text-center">
              Entered incorrect details?
            </span>
            <span
              className="text-xs text-primary underline cursor-pointer font-medium"
              onClick={handleDiscardAndRetry}
            >
              Discard and Retry
            </span>
          </div>

          {/* Continue to Offline Option */}
          <div className="flex flex-col items-center gap-2 mt-2 pt-4 border-t border-brand-borderLightGray">
            <p className="text-xs text-brand-textBlack text-center leading-4">
              To continue with name mismatch, switch to offline mode.
              You&apos;ll need to submit KYC documents at the bank for
              verification.
            </p>
            <span
              className="text-xs text-primary underline cursor-pointer font-medium"
              onClick={handleContinueToOffline}
            >
              Switch to Offline Verification
            </span>
          </div>
        </div>
      </div>
    </CustomModalLayout>
  );
};
